import { createSelector, createSelectorCreator, defaultMemoize } from "reselect";
import _ from "lodash";
import isEqual from "react-fast-compare";

import { userFeedsSelector } from "../Feeds/selectors";
import { disabledFeedsSelector } from "../../AppState/Selectors";
import { processAggregates } from "../../SharedComponents/trans-utils";
export { collectionsSelector, makeGetCollection, makeGetCollectionMembers } from "../Collections/selectors";
export { rulesSelector } from "../Rules/selectors";
export { userFeedsSelector } from "../Feeds/selectors";
export * from "../GIS/selectors";
export { floorPlanSelector } from "../FloorPlan/selectors";
export {
	eventsSelector,
	eventTypesSelector,
	currentEventsSelector,
	currentOwnedEventsSelector,
	activeOwnedEventsSelector,
	eventsSharedFromOrgSelector,
	eventsSharedFromEcoSelector,
	sharedEventsSelector,
	activeSharedEventsSelector,
	availableEventsSelector,
	getEventsSelector,
	getEventsWithoutFiltersSelector,
	activeEventsSelector,
	makeGetEvent,
	makeGetPinnedItems,
	templatesSelector,
	ownedTemplatesSelector,
	templatesSharedFromOrgSelector,
	templatesSharedFromEcoSelector,
	sharedTemplatesSelector,
	availableTemplatesSelector,
	closedEventsSelector,
	scheduledEventsSelector,
	sortedStarredTemplatesSelector,
	eventDialogTemplatesSelector
} from "../Events/selectors";
export { getUnassignedMembers } from "../UnitMembers/selectors";
export { unitMemberSelector, getAllUnits, getUnits, unitMemberMemoized } from "../Units/selectors";
export { getPlaySettingByStream, getPlaySettingsByStreamAndValue } from "../WowzaWebRTC/PlaySettings/selectors";
export { getWebrtcSettingByStream, getWebrtcSettingByStreamAndValue } from "../WowzaWebRTC/WebRTCPlay/selectors";
export { getEntityIconsByGroup, getEntityIconsByType, getEntityIconsByGroupForPoints } from "../EntityIcons/selectors";
export { getRuleConditionsByConditionId, getRuleConditionsByConditionType } from "../RuleConditions/selectors";
export { hasDevicesOrCamerasFeeds } from "../Devices/selectors";

export const globalGeo = state => state.globalGeo;
const userIdSelector = state => (state.session ? state.session.user.profile.id : null);
export const orgRoleSelector = state => state.session.user.profile.orgRole;
export const globalData = (state, props) => {
	return props && props.feedId ? state.globalData[props.feedId] : state.globalData;
};

const notifications = state => state.globalData.notifications;
const alerts = state => state.globalData.alerts;
const floorPlans = state => state.globalData.floorPlanWithFacilityFeedId?.floorPlans;
const selectedSitePlan = state => state.appState.persisted?.selectedSitePlan;

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const selectAggregatedActiveAlerts = createDeepEqualSelector(
  state => state.globalData.alerts.activeItemsById,
  activeItemsById => processAggregates(activeItemsById)
);

export const notificationById = id =>
  createSelector(
    state => state.globalData.notifications.activeItemsById,
    state => state.globalData.notifications.archiveItemsById,
    selectAggregatedActiveAlerts,
    state => state.globalData.alerts.archiveItemsById,
    (active, archive, aggregatedActive, alertsArchive) => {
      return (
        active?.[id] ??
        archive?.[id] ??
        aggregatedActive?.[id] ??
        alertsArchive?.[id]
      );
    }
  );

// export const alertById = (id) =>
// 	createSelector(alerts, (alerts) => {
// 		const { activeItemsById, archiveItemsById } = alerts;
// 		const allAlerts = { ...activeItemsById, ...archiveItemsById };
// 		return allAlerts[id];
// 	});

export const priorityNotificationSelector = createSelector(alerts, alerts => {
	// const { activeItemsById } = alerts;
	// const alerts = _.pickBy(activeItemsById, (item) => item.isPriority);
	return alerts?.activeItemsById || {};
});

export const userFacilitySelector = createSelector(globalData, globalData => {
	const userFacilities = [];
	_.forOwn(globalData, value => {
		if (value.data && !_.isEmpty(value.data)) {
			_.forOwn(value.data, facility => {
				if (facility.entityType === "facility") {
					userFacilities.push(facility);
				}
			});
		}
	});
	return userFacilities;
});

export const activeAlertsSelector = createSelector(notifications, notifications => {
	const { activeItemsById } = notifications;
	const alerts = _.map(
		_.pickBy(activeItemsById, item => item.isPriority && !item.viewed),
		item => _.size(item.object) && item.object.id
	);
	return alerts;
});

const integrations = state => {
	const { integrations } = state.session.user.profile;
	return integrations;
};

export const feedInfoSelector = feedId =>
	createSelector(integrations, feeds => {
		const feed = feeds.find(feed => feedId === feed.feedId);
		return feed;
	});

// Returns all non-disabled feeds a user has access to.
export const userIntegrations = createSelector(userFeedsSelector, disabledFeedsSelector, (userFeeds, disabled) => {
	const integrations = _.pickBy(userFeeds, integration => integration.canView && !disabled.includes(integration));
	return integrations;
});

// Grabs all entities, minus GeoJSON, from feeds you have access to and are not disabled
// Data Type: Array
export const feedEntitiesSelector = createDeepEqualSelector(
	globalData,
	userIntegrations,
	(globalData, integrations) => {
		let entities = {};
		_.each(
			_.pickBy(integrations, integration => globalData[integration.feedId]),
			integration => {
				entities = {
					...entities,
					...globalData[integration.feedId].data
				};
			}
		);
		return entities;
	}
);

//Need to combine this with the above selector and refactor it to support both global data and global geo.
export const feedEntitiesSelectorUsingGlobalGeoData = createDeepEqualSelector(
	globalGeo,
	userIntegrations,
	(globalGeo, integrations) => {
		let entities = {};
		_.each(
			_.pickBy(integrations, integration => globalGeo && globalGeo[integration.feedId]),
			integration => {
				entities = {
					...entities,
					...globalGeo[integration.feedId].data
				};
			}
		);
		return entities;
	}
);

export const feedEntitiesByTypeSelector = entityType =>
	createDeepEqualSelector(globalData, userIntegrations, (globalData, integrations) => {
		let entities = {};
		_.each(
			_.pickBy(
				integrations,
				integration => integration.entityType === entityType && globalData[integration.feedId]
			),
			integration => {
				entities = {
					...entities,
					...globalData[integration.feedId].data
				};
			}
		);
		return entities;
	});

export const feedEntitiesGeoByTypeSelector = entityType =>
	createDeepEqualSelector(globalGeo, userIntegrations, (globalGeo, integrations) => {
		let entities = {};
		_.each(
			_.pickBy(
				integrations,
				integration => integration.entityType === entityType && globalGeo?.[integration.feedId]
			),
			integration => {
				entities = {
					...entities,
					...globalGeo?.[integration.feedId].data
				};
			}
		);
		return entities;
	});

export const feedEntitiesWithGeoByTypeSelector = entityType =>
	createDeepEqualSelector(globalData, globalGeo, userIntegrations, (globalData, globalGeo, integrations) => {
		let entities = {};
		_.each(
			_.pickBy(
				integrations,
				integration => integration.entityType === entityType && globalData[integration.feedId]
			),
			integration => {
				entities = {
					...entities,
					..._.merge({}, globalGeo[integration.feedId].data, globalData[integration.feedId].data)
				};
			}
		);
		return entities;
	});

/**
 * Grab collection members from available entities based on entity ID
 * @param {Array} members - An array of entity IDs
 */
export const collectionMemberSelector = members => {
	return createDeepEqualSelector(feedEntitiesSelector, entities => {
		const filteredMembers = _.pickBy(_.cloneDeep(entities), entity => _.includes(members, entity.id));
		return filteredMembers;
	});
};

export const sharedEntitiesSelector = createSelector(userIdSelector, feedEntitiesSelector, (userId, entities) => {
	const sharedEntities = _.pickBy(entities, entity => {
		const { owner } = entity;
		return owner && owner !== userId;
	});
	return sharedEntities;
});

// Grabs the GeoJSON for all entities from feeds you have access to and are not disabled
// Data Type: Object
export const feedEntityGeoSelector = createSelector(globalGeo, userIntegrations, (globalGeo, integrations) => {
	let geoObj = {};
	_.each(
		_.filter(integrations, integration => (globalGeo ? globalGeo[integration.feedId] : false)),
		integration => {
			geoObj = {
				...geoObj,
				...(globalGeo ? globalGeo[integration.feedId].data : {})
			};
		}
	);
	return geoObj;
});

export const getEntityId = (state, props) => (props.entity ? props.entity.id : null);

export const getGeo = createSelector(feedEntityGeoSelector, getEntityId, (geos, id) => {
	// cSpell:ignore geos
	const geo = geos[id];

	return geo ? geo : null;
});

export const getGeoMemoized = createDeepEqualSelector(getGeo, geo => geo);

export const getFeedId = (state, props) => props.feedId;

const globalDataSelector = createDeepEqualSelector(globalData, globalData => globalData);

export const feedDataSelector = createDeepEqualSelector(globalDataSelector, global => {
	return global ? global.data : {};
});

export const feedSelector = (state, props) => {
	if (props && state.globalData[props.feedId] && state.globalData[props.feedId].data) {
		return state.globalData[props.feedId].data;
	}
};

const feedGeoSelector = (state, props) => {
	if (props && state.globalGeo?.[props.feedId] && state.globalGeo[props.feedId].data) {
		return state.globalGeo[props.feedId].data;
	}
};

// const notificationSelector = (state) => {
// 	if (state.globalData && state.globalData.notifications && state.globalData.notifications.activeItemsById) {
// 		return state.globalData.notifications.activeItemsById;
// 	}
// };

export const alertStateSelector = state => {
	if (state.globalData && state.globalData.notifications) {
		return state.globalData.notifications.initial;
	}
};

export const alertSelector = createDeepEqualSelector(priorityNotificationSelector, alerts => {
	// return _.pickBy(notifications, (notification) => notification.isPriority && notification.viewed === false);
	return alerts;
});

export const layerSourcesSelector = createSelector(feedGeoSelector, geo => {
	return geo ? geo : {};
});

const getEntityById = (state, props) => {
	const { id, feedId } = props;
	const entity = state.appState["mapRef"].entities[feedId] ? state.appState["mapRef"].entities[feedId][id] : null;
	return entity;
};

export const makeGetEntity = () => {
	return createDeepEqualSelector(getEntityById, entity => {
		return entity;
	});
};

const _userFeedsByTypeSelector = (state, entityType) => {
	if (!state.session) return {};
	return _.filter(state.session.userFeeds, feed => feed && feed.entityType === entityType);
};

export const userFeedsByTypeSelector = createSelector([_userFeedsByTypeSelector], feeds => {
	return feeds;
});

export const getFeedEntitiesByProperty = (state, feedId, property, value) => {
	if (state.globalData[feedId] && state.globalData[feedId].data) {
		const globalData = state.globalData[feedId].data;
		const items = _.filter(globalData, feed => feed && feed.entityData.properties[property] === value);
		return items;
	}
};

export const getAnimationEntities = state => (forReplay, replayAlerts, timeData) => {
	const alerts = forReplay ? replayAlerts : state.globalData?.alerts?.activeItemsById;
	const unacknowledgedAlerts = Object.values(alerts)?.filter(alert => alert.occurrenceCount !== alert.ackedCount);
	const animationEntities = {};

	unacknowledgedAlerts.forEach(alert => {
		const { object, target } = alert.activity || {};

		if ((object?.entity?.feedId || object?.feedId) && object?.id) {
			const feedId = object?.entity?.feedId || object?.feedId;
			const id = object?.id;
			const entity = forReplay
				? timeData[id]
				: feedId === "event"
					? state.globalData?.events?.events?.[id]
					: state.globalGeo[feedId]?.data?.[id];

			if (entity?.entityData?.geometry?.type == "Point")
				animationEntities[id] = {
					entityType: entity.entityType,
					feedId: feedId,
					geometry: entity.entityData?.geometry,
					alertCount: animationEntities[id]?.alertCount ? animationEntities[id]?.alertCount + 1 : 1
				};
		}
		if ((target?.entity?.feedId || target?.feedId) && target?.id) {
			const feedId = target?.entity?.feedId || target?.feedId;
			const id = target?.id;
			const entity = forReplay
				? timeData[id]
				: feedId === "event"
					? state.globalData?.events?.events?.[id]
					: state.globalGeo[feedId]?.data?.[id];

			if (entity?.entityData?.geometry?.type == "Point")
				animationEntities[id] = {
					entityType: entity.entityType,
					feedId: feedId,
					geometry: entity.entityData?.geometry,
					alertCount: animationEntities[id]?.alertCount ? animationEntities[id]?.alertCount + 1 : 1
				};
		}
	});
	return animationEntities;
};

export const animationEntitiesSelector = createSelector(getAnimationEntities, entities => {
	return entities || {};
});

export const activeAlertingZoneDataSelector = createSelector([alerts], alerts =>
	Object.values(alerts?.activeItemsById || {}).flatMap(item => ([...(item.alertingZones || []), ...(item?.activity?.target?.alertingZones || [])]))
);

const EMPTY_ARRAY = [];

export const alertNotificationsSelector = createDeepEqualSelector(alerts, alerts => {
	if (!alerts) return EMPTY_ARRAY;

	const active = alerts.activeItems.map(id => alerts.activeItemsById[id]);
	const archived = alerts.archiveItems.map(id => alerts.archiveItemsById[id]);

	const combined = active.concat(archived);

	return combined.slice().sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
});

export const activeNotificationsSelector = createSelector(alertNotificationsSelector, notifications =>
	notifications.filter(n => n?.active)
);

export const sitePlanSelector = createSelector(floorPlans, selectedSitePlan, (floorPlans, selectedSitePlan) => {
	const sitePlan = selectedSitePlan ? floorPlans[selectedSitePlan] : null;

	return sitePlan;
});

const selectEcoSettings = state => state.ecoSettings || [];

// Selector to check if any item has alertConcurrenceEnabled as true
export const selectIsAlertConcurrenceEnabled = createSelector([selectEcoSettings], ecoSettings =>
	ecoSettings.some(setting => setting.key === "alertConcurrenceEnabled" && setting.value === true)
);
const selectOrgRoles = state => state.session.user.profile.orgRoles;

export const selectAllowAlertConcurrence = createSelector([selectOrgRoles], orgRoles =>
	orgRoles.some(role => role.allowAlertConcurrence === true)
);

export const aggregateAlertsSelector = createSelector([alerts], alerts => contextId => {
	const allAlerts = alerts?.activeItemsById || {};
	const alert = allAlerts[contextId];
	return alert ? [alert] : [];
});

export const selectAlertOccurrences = contextId =>
	createSelector([alerts], alerts => {
		const allAlerts = alerts?.activeItemsById || {};
		const alert = allAlerts[contextId];
		if (!alert?.occurrences?.length) return [];
		return [...alert.occurrences].sort((a, b) =>
			new Date(a.createdDate) - new Date(b.createdDate)
		);
	});

const selectActiveAlerts = state => state.globalData.alerts.activeItemsById ?? {};

export const selectAggregateAlertCountById = id =>
	createSelector([selectActiveAlerts], allAlerts => {
		if (!id) return 0;
		const alert = allAlerts[id];
		if (!alert) return 0;
		return alert.occurrenceCount || 1;
	});

export const getEntityName = createSelector(
	[state => state.globalData, state => state.session.userFeeds, (_, id) => id, (_, __, entityType) => entityType],
	(globalData, userFeeds, id, entityType) => {
		if (!globalData || !userFeeds || !id || !entityType) return null;

		const normalizedType = entityType.toLowerCase();

		// Feeds the user can view that match the requested entity type
		const candidateFeedIds = Object.values(userFeeds)
			.filter(feed => feed.entityType.toLowerCase() === normalizedType)
			.map(feed => feed.feedId)
			.filter(Boolean);

		// If nothing matched by entityType, bail early (or fallback to all feeds if you prefer)
		if (!candidateFeedIds.length) {
			return null;
		}

		// Search only the relevant feeds for the matching entity ID
		for (const feedId of candidateFeedIds) {
			const data = globalData[feedId]?.data;
			if (!data) continue;

			if (Object.hasOwn(data, id)) {
				const entity = data[id];
				return entity?.entityData?.properties?.name || null;
			}
		}

		return null;
	}
);
